import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import ErrorBoundary from "./components/ErrorBoundary";
import AssistantWidget from "./components/AssistantWidget";
import WelcomeMission from "./components/WelcomeMission";
import SoundBootstrap from "./components/SoundBootstrap";
import StartupIntro from "./components/StartupIntro";
import OfflineOverlay from "./components/OfflineOverlay";
import NativeBridge from "./components/NativeBridge";
import { initAppShell } from "./lib/native/shell";
import Login from "./pages/Login";
import Legal from "./pages/Legal";
import Explore from "./pages/Explore";
import EsploraHub from "./pages/customer/EsploraHub";
import HomeSmart from "./pages/smart/HomeSmart";
import BusinessList from "./pages/smart/BusinessList";
import EventsList from "./pages/smart/EventsList";
import FavoritesList from "./pages/smart/FavoritesList";
import { homePath } from "./lib/homeMode";
import Work from "./pages/Work";
import Experiences from "./pages/Experiences";
import MyExperiences from "./pages/customer/MyExperiences";
import ExperienceDetail from "./pages/customer/ExperienceDetail";
import SharedExperience from "./pages/SharedExperience";
import ResetPassword from "./pages/ResetPassword";
import AttivaAccount from "./pages/AttivaAccount";
import ForcePasswordChange from "./components/ForcePasswordChange";
import BusinessPage from "./pages/customer/BusinessPage";
import ProList from "./pages/pro/ProList";
import ProPublic from "./pages/pro/ProPublic";
import ProRegister from "./pages/pro/ProRegister";
import ProDashboard from "./pages/pro/ProDashboard";
import Checkout from "./pages/customer/Checkout";
import OrderTracking from "./pages/customer/OrderTracking";
import CustomerOrders from "./pages/customer/CustomerOrders";
import CustomerProfile from "./pages/customer/CustomerProfile";
import Discover from "./pages/customer/Discover";
import MyActivity from "./pages/customer/MyActivity";
import Passaporto from "./pages/customer/Passaporto";
import FumaDeals from "./pages/customer/FumaDeals";
import Growth from "./pages/customer/Growth";
import Reservations from "./pages/customer/Reservations";
import CustomerQuotes from "./pages/customer/CustomerQuotes";
import CustomerBookings from "./pages/customer/CustomerBookings";
import MarketErrands from "./pages/customer/MarketErrands";
import UserMissions from "./pages/customer/UserMissions";
const RiderRun = React.lazy(() => import("./pages/game/RiderRun"));
const RiderRunLeaderboard = React.lazy(() => import("./pages/game/RiderRunLeaderboard"));
const RiderRunSkins = React.lazy(() => import("./pages/game/RiderRunSkins"));
import Leaderboard from "./pages/customer/Leaderboard";
import RewardsLevels from "./pages/RewardsLevels";
import Notifications from "./pages/customer/Notifications";
import PaymentResult from "./pages/customer/PaymentResult";
import BusinessDashboard from "./pages/business/BusinessDashboard";
import RiderDashboard from "./pages/rider/RiderDashboard";
import OpsDashboard from "./pages/ops/OpsDashboard";
import CeoDashboard from "./pages/ceo/CeoDashboard";
import CeoPreviewBanner, { isCeoPreview } from "./components/CeoPreviewBanner";

function Loader() {
  return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0C] text-[#00E676] font-display font-black text-xl">Fuma c'anduma…</div>;
}

const HOME_BY_ROLE = {
  customer: "/esplora", business: "/business", rider: "/rider", ops: "/ops", ceo: "/ceo", admin: "/ceo", professionista: "/pro",
};

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (roles && !roles.includes(user.role)) {
    const previewOk = isCeoPreview() && (user.role === "ceo" || user.role === "admin");
    if (!previewOk) return <Navigate to={HOME_BY_ROLE[user.role] || "/login"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to={homePath(null)} replace />;
  if (user.role === "customer") return <Navigate to={homePath(user)} replace />;
  return <Navigate to={HOME_BY_ROLE[user.role] || "/esplora"} replace />;
}

function App() {
  useEffect(() => { initAppShell(); }, []);
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-right" richColors />
            <StartupIntro />
            <OfflineOverlay />
            <NativeBridge />
            <CeoPreviewBanner />
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/esplora" element={<EsploraHub />} />
              <Route path="/esplora/mappa" element={<Explore />} />
              <Route path="/home" element={<HomeSmart />} />
              <Route path="/ai" element={<Navigate to="/home" replace />} />
              <Route path="/lista/food" element={<BusinessList variant="food" />} />
              <Route path="/lista/attivita" element={<BusinessList variant="activities" />} />
              <Route path="/lista/vicino" element={<BusinessList variant="nearby" />} />
              <Route path="/lista/eventi" element={<EventsList />} />
              <Route path="/lista/preferiti" element={<FavoritesList />} />
              <Route path="/lavoro" element={<Protected roles={["customer"]}><Work /></Protected>} />
              <Route path="/esperienze" element={<Experiences />} />
              <Route path="/esperienze/condivisa/:token" element={<SharedExperience />} />
              <Route path="/professionisti" element={<ProList />} />
              <Route path="/professionisti/registrazione" element={<ProRegister />} />
              <Route path="/professionisti/:id" element={<ProPublic />} />
              <Route path="/pro/*" element={<Protected roles={["professionista"]}><ProDashboard /></Protected>} />
              <Route path="/attivita/:id" element={<BusinessPage />} />
              {/* Deep / Universal Links: stessi URL su web (senza app) e in-app */}
              <Route path="/order/:id" element={<Protected roles={["customer"]}><OrderTracking /></Protected>} />
              <Route path="/tracking/:id" element={<Protected roles={["customer"]}><OrderTracking /></Protected>} />
              <Route path="/business/:id" element={<BusinessPage />} />
              <Route path="/promo/:id" element={<FumaDeals />} />
              <Route path="/professional/:id" element={<ProPublic />} />
              <Route path="/login" element={<Login />} />
              <Route path="/legal/:doc" element={<Legal />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/attiva" element={<AttivaAccount />} />
              <Route path="/app" element={<Navigate to="/esplora" replace />} />
              <Route path="/app/business/:id" element={<Protected roles={["customer"]}><BusinessPage /></Protected>} />
              <Route path="/app/checkout" element={<Protected roles={["customer"]}><Checkout /></Protected>} />
              <Route path="/app/orders" element={<Protected roles={["customer"]}><CustomerOrders /></Protected>} />
              <Route path="/app/orders/:id" element={<Protected roles={["customer"]}><OrderTracking /></Protected>} />
              <Route path="/app/esperienze" element={<Protected roles={["customer"]}><MyExperiences /></Protected>} />
              <Route path="/app/esperienze/:id" element={<Protected roles={["customer"]}><ExperienceDetail /></Protected>} />
              <Route path="/app/profile" element={<Protected roles={["customer"]}><CustomerProfile /></Protected>} />
              <Route path="/app/scopri" element={<Discover />} />
              <Route path="/app/attivita" element={<Protected roles={["customer"]}><MyActivity /></Protected>} />
              <Route path="/app/passaporto" element={<Protected roles={["customer"]}><Passaporto /></Protected>} />
              <Route path="/app/deals" element={<Protected roles={["customer"]}><FumaDeals /></Protected>} />
              <Route path="/app/regala" element={<Protected roles={["customer"]}><Growth /></Protected>} />
              <Route path="/app/prenotazioni" element={<Protected roles={["customer"]}><Reservations /></Protected>} />
              <Route path="/app/quotes" element={<Protected roles={["customer"]}><CustomerQuotes /></Protected>} />
              <Route path="/app/bookings" element={<Protected roles={["customer"]}><CustomerBookings /></Protected>} />
              <Route path="/app/mercato" element={<Protected roles={["customer"]}><MarketErrands /></Protected>} />
              <Route path="/app/missioni" element={<Protected roles={["customer"]}><UserMissions /></Protected>} />
              <Route path="/app/rider-run" element={<Protected roles={["customer"]}><React.Suspense fallback={<div className="min-h-screen bg-[#0A0A0C]" />}><RiderRun /></React.Suspense></Protected>} />
              <Route path="/app/rider-run/classifica" element={<Protected roles={["customer"]}><React.Suspense fallback={<div className="min-h-screen bg-[#0A0A0C]" />}><RiderRunLeaderboard /></React.Suspense></Protected>} />
              <Route path="/app/rider-run/skins" element={<Protected roles={["customer"]}><React.Suspense fallback={<div className="min-h-screen bg-[#0A0A0C]" />}><RiderRunSkins /></React.Suspense></Protected>} />
              <Route path="/app/classifica" element={<Protected roles={["customer"]}><Leaderboard /></Protected>} />
              <Route path="/app/livelli" element={<Protected roles={["customer","rider","business","professionista"]}><RewardsLevels /></Protected>} />
              <Route path="/app/notifiche" element={<Protected roles={["customer"]}><Notifications /></Protected>} />
              <Route path="/payment/success" element={<Protected roles={["customer"]}><PaymentResult mode="success" /></Protected>} />
              <Route path="/payment/cancel" element={<Protected roles={["customer"]}><PaymentResult mode="cancel" /></Protected>} />
              <Route path="/business/*" element={<Protected roles={["business"]}><BusinessDashboard /></Protected>} />
              <Route path="/rider/*" element={<Protected roles={["rider"]}><RiderDashboard /></Protected>} />
              <Route path="/ops/*" element={<Protected roles={["ops","ceo","admin"]}><OpsDashboard /></Protected>} />
              <Route path="/ceo/*" element={<Protected roles={["ceo","admin"]}><CeoDashboard /></Protected>} />
              <Route path="*" element={<RootRedirect />} />
            </Routes>
            <AssistantWidget />
            <WelcomeMission />
            <SoundBootstrap />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
